import { useState, useEffect } from 'react';
import { hasFeatureEnabled } from '../../../../services/feature';
import { callEndpoint } from '../../../../services/backend';
import useIsInBounds from '../../../hooks/use-is-in-bounds';

interface Coordinates {
  lat?: number;
  lon?: number;
}

const transformRadioChannels = (response: { texttodisplay?: string }[]) => {
  return response
    ?.flatMap((radioInfo) => {
      // Extract all numbers from the text
      const matches = radioInfo?.texttodisplay?.match(/\d+/g) || [];
      return matches.map((num) => parseInt(num, 10));
    })
    .filter(Boolean) // Remove nulls
    .filter((channel, index, array) => array.indexOf(channel) === index) // Remove duplicates
    .sort((a, b) => a - b) // Sort numerically ascending
    .join(', ');
};

export { transformRadioChannels };

export const useRadioRanges = (coordinates: Coordinates | null) => {
  const [radioChannels, setRadioChannels] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);
  const canPrepareWa = useIsInBounds('WESTERNAUSTRALIA');

  useEffect(() => {
    const getRadioRanges = async () => {
      if (!coordinates?.lat || !coordinates?.lon) {
        return;
      }

      if (hasFeatureEnabled('vhf-radio-ranges') && canPrepareWa) {
        setIsLoading(true);
        try {
          const response: { texttodisplay?: string }[] = await callEndpoint(
            callEndpoint.endpoints.getRadioRanges,
            {
              ':latitude': coordinates.lat,
              ':longitude': coordinates.lon,
            },
          );

          setRadioChannels(transformRadioChannels(response));
        } finally {
          setIsLoading(false);
        }
      }
    };
    getRadioRanges();
  }, [coordinates?.lat, coordinates?.lon, canPrepareWa]);

  return {
    radioChannels: radioChannels ? `VHF ${radioChannels}` : undefined,
    isEnabled: hasFeatureEnabled('vhf-radio-ranges'),
    isLoading,
    isAvailable: hasFeatureEnabled('vhf-radio-ranges') && canPrepareWa,
  };
};
